import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { useCampaignFiles } from '@/app/module/campaigns/component/campaign-files-provider'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { FileType } from '@/app/types'

import { toCampaignFileError } from './campaign-file-error'
import {
  getCampaignFileDownloadPath,
  getCampaignFileSignPathsApiArg,
  getCampaignFileSignRequestKey,
  getPendingCampaignFileSignPaths,
  getSignedCampaignFileUrl,
} from './campaign-file-url'

export type GetCampaignFilePayload = {
  file: string
  msgId: string
}

export const useGetCampaignFile = () => {
  const { startLoading, updateFile, finishLoading } = useCampaignFiles()
  const orgId = useSelector(selectOrgId)
  const [signPaths, signPathsResponse] = esApi.endpoints.signPaths.useMutation()

  const getFile = useCallback(
    async ({ file, msgId }: GetCampaignFilePayload): Promise<FileType | Error> => {
      startLoading(msgId)

      try {
        const signedPath = getCampaignFileDownloadPath({ orgId, filename: file })
        const signedRes = await getPendingCampaignFileSignPaths(
          getCampaignFileSignRequestKey({ orgId, filename: file }),
          () => signPaths(getCampaignFileSignPathsApiArg(signedPath)).unwrap(),
        )
        const url = getSignedCampaignFileUrl({ signedPath, urlToken: signedRes[signedPath] })
        const result = {
          filename: file,
          title: file,
          url,
        }

        updateFile({
          id: msgId,
          filename: file,
          url,
        })

        return result
      } catch (error) {
        const message = getErrorMessage(error, 'Failed to download file')

        finishLoading(msgId)

        return toCampaignFileError(error, message)
      }
    },
    [orgId, finishLoading, signPaths, startLoading, updateFile],
  )

  return [
    getFile,
    {
      ...signPathsResponse,
    },
  ] as const
}
